import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CryptoService } from '../services/crypto.service';
import {
  PlatformTaskInterfaceContext,
  PlatformTaskInterfaceStateService,
} from '../services/platform-task-interface-state.service';
import { PLATFORM_CHAT_ENTITY_TYPE } from '../components/platform-comments-chat/platform-chat-entity.const';
import { contentUnavailableTree } from '../utils/content-unavailable.utils';
import { WorkspaceService } from '../services/workspace.service';
import { WorkspaceDTO } from '../interfaces/workspace.interface';
import { AuthService } from '../services/auth.service';
import { Brand } from '../interfaces/brand/brand.interface';
import { brandHasWorkspace } from '../utils/brand-workspace.utils';
import { TimelineService } from '../services/timeline.service';

export const canAccessWorkspaceTask: CanActivateFn = (route, state) => {
  const router: Router = inject(Router);
  const authService: AuthService = inject(AuthService);
  const cryptoService: CryptoService = inject(CryptoService);
  const taskStateService: PlatformTaskInterfaceStateService = inject(PlatformTaskInterfaceStateService);
  const workspaceService: WorkspaceService = inject(WorkspaceService);
  const timelineService: TimelineService = inject(TimelineService);
  const { workspaceId } = route.params;

  if (!workspaceId) {
    return contentUnavailableTree(router);
  }

  const actualWorkspaceId: string = cryptoService.decryptUsingAES256(workspaceId);

  if (!actualWorkspaceId) {
    return contentUnavailableTree(router);
  }

  const context: PlatformTaskInterfaceContext = taskStateService.getCurrentValue();

  if (context.pageId === actualWorkspaceId && context.entityType === PLATFORM_CHAT_ENTITY_TYPE.WORKSPACE) {
    timelineService.activeCampaign$.next(null);
    timelineService.driveCampaignData = null;
    return true;
  }

  const enterWorkspace = (workspace: WorkspaceDTO): true => {
    taskStateService.setContext({
      pageId: actualWorkspaceId,
      entityType: PLATFORM_CHAT_ENTITY_TYPE.WORKSPACE,
      entityName: workspace.name,
    });
    timelineService.activeCampaign$.next(null);
    timelineService.driveCampaignData = null;
    return true;
  };

  const activeWorkspace: WorkspaceDTO = workspaceService.getActiveWorkspace();
  if (activeWorkspace?.id === actualWorkspaceId) {
    return enterWorkspace(activeWorkspace);
  }

  const brand: Brand = authService.getUserBrand();
  // The workspace may belong to another brand (e.g. a bookmarked URL after a brand switch)
  if (!brandHasWorkspace(brand, actualWorkspaceId)) {
    return contentUnavailableTree(router);
  }

  return workspaceService.getWorkspaceById(brand.companyId, actualWorkspaceId).pipe(
    map((workspace: WorkspaceDTO) => {
      workspaceService.setActiveWorkspace(workspace);
      return enterWorkspace(workspace);
    }),
    catchError(() => of(contentUnavailableTree(router)))
  );
};
